import { useMemo } from "react";
import {
  Calendar,
  FileBarChart,
  Globe,
  Home,
  LineChart,
  Package,
  Receipt,
  Rss,
  Shield,
  Sparkles,
  type LucideIcon,
} from "lucide-react";

import { useUser } from "@/auth/UserContext";
import {
  AIS_VESSEL_TRACKING,
  INVOICE_RECONCILIATION,
  useFeatureEnabled,
} from "@/context/FeatureFlagsContext";

export interface NavItem {
  id: string;
  label: string;
  icon: LucideIcon;
  path: string;
}

export const navItems: NavItem[] = [
  { id: "kai", label: "Kai", icon: Sparkles, path: "/" },
  { id: "dashboard", label: "Home", icon: Home, path: "/dashboard" },
  { id: "feed", label: "Feed", icon: Rss, path: "/feed" },
  { id: "bookings", label: "Bookings", icon: Package, path: "/bookings" },
  { id: "rates", label: "Rates", icon: LineChart, path: "/rates" },
  { id: "schedule", label: "Schedule", icon: Calendar, path: "/schedule" },
  { id: "invoices", label: "Invoices", icon: Receipt, path: "/invoices" },
  { id: "tracking", label: "Tracking", icon: Globe, path: "/tracking" },
  { id: "reports", label: "Reports", icon: FileBarChart, path: "/reports" },
  { id: "admin", label: "Admin", icon: Shield, path: "/admin" },
];

/** Primary bottom-nav slots. Overflow destinations live behind More. */
export const MOBILE_PRIMARY_NAV_IDS = [
  "kai",
  "dashboard",
  "feed",
  "reports",
] as const;

/** Destinations opened from the mobile More sheet. */
export const MOBILE_MORE_NAV_IDS = [
  "bookings",
  "rates",
  "schedule",
  "invoices",
  "tracking",
  "admin",
] as const;

const MOBILE_PRIMARY_NAV_ID_SET = new Set<string>(MOBILE_PRIMARY_NAV_IDS);
const MOBILE_MORE_NAV_ID_SET = new Set<string>(MOBILE_MORE_NAV_IDS);

export function isMobileNavItem(id: string): boolean {
  return MOBILE_PRIMARY_NAV_ID_SET.has(id);
}

export function isMobileMoreNavItem(id: string): boolean {
  return MOBILE_MORE_NAV_ID_SET.has(id);
}

/** Nav items visible for the current tenant (feature-flagged entries filtered). */
export function useVisibleNavItems(): NavItem[] {
  const trackingEnabled = useFeatureEnabled(AIS_VESSEL_TRACKING);
  const invoicesEnabled = useFeatureEnabled(INVOICE_RECONCILIATION);
  const { isAdmin, isSalesPerson, isClient, permissions } = useUser();
  const canReadInvoices = permissions.includes("read:invoices");
  return useMemo(
    () =>
      navItems.filter((item) => {
        if (item.id === "tracking" && !trackingEnabled) return false;
        if (
          item.id === "invoices" &&
          (!invoicesEnabled || isSalesPerson || isClient || !canReadInvoices)
        ) {
          return false;
        }
        if (item.id === "admin" && !isAdmin) return false;
        // Client (consignee portal) — no rates or opportunity surfaces.
        if (isClient && (item.id === "rates" || item.id === "reports")) {
          return false;
        }
        return true;
      }),
    [
      isAdmin,
      trackingEnabled,
      invoicesEnabled,
      isSalesPerson,
      isClient,
      canReadInvoices,
    ],
  );
}

/** Resolve active nav id from pathname. */
export function activeNavId(pathname: string): string {
  if (pathname === "/") return "kai";
  if (pathname === "/preview" || pathname.startsWith("/dashboard")) {
    return "dashboard";
  }
  const match = navItems.find(
    (item) => item.path !== "/" && pathname.startsWith(item.path),
  );
  return match?.id ?? "kai";
}
